import React, { useState } from 'react';
import { ArrowLeft, FileText, BarChart3, Clock, Landmark } from 'lucide-react';
import { ClientDetailData } from '../../../../hooks/useClientDetail';
import type { ClientDetailPeriodBundle } from './client-detail-period-types';
import { InvoiceDetailsTab } from './InvoiceDetailsTab';
import { TimesheetProjectsTab } from './TimesheetProjectsTab';
import { ChartsAndTrendsTab } from './ChartsAndTrendsTab';
import { DepositInfoTab } from './DepositInfoTab';

/* ─── Tabs config ────────────────────────────────────────────────────────── */

const TABS = [
  { id: 0, label: 'Invoice Details', icon: FileText },
  { id: 1, label: 'Timesheet Projects', icon: Clock },
  { id: 2, label: 'Charts & Trends', icon: BarChart3 },
  { id: 3, label: 'Deposit Info', icon: Landmark },
] as const;

const formatSinceLabel = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Sticky page header row: back + name / id + tags under name.
 * Billing-entity cards live beside Invoice Details filters.
 */
export const ClientDetailHeaderBar: React.FC<{
  data: ClientDetailData;
  onBack: () => void;
}> = ({ data, onBack }) => {
  const basicInfo = data?.contact_profile?.basic_info;
  const baseCurrency =
    data?.metadata?.original_currency ||
    data?.currency_analysis?.primary_currency ||
    'USD';

  if (!basicInfo) return null;

  const statusRaw = String(basicInfo.activation_status || '').toLowerCase();
  const isActive = statusRaw === 'active' || statusRaw === 'activate';

  return (
    <div className="flex gap-2.5 sm:gap-3 min-w-0 w-full items-center">
      <div className="flex gap-2.5 sm:gap-3 min-w-0 flex-1">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-white hover:text-slate-900 transition-colors flex-none self-center shrink-0 bg-white/80"
        >
          <ArrowLeft size={13} /> Back
        </button>

        <div className="min-w-0 flex-1 py-0.5">
          <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
            <h1 className="text-base sm:text-[17px] font-bold text-[#0D0D0D] leading-snug">
              {basicInfo.contact_name}
            </h1>
            {basicInfo.contact_id != null && basicInfo.contact_id !== '' && (
              <span className="text-[11px] text-slate-600 font-mono shrink-0">
                C{basicInfo.contact_id}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span
              className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide ${
                isActive
                  ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                  : 'bg-red-50 text-red-600 ring-1 ring-red-200'
              }`}
            >
              {isActive ? '● Active' : basicInfo.activation_status || 'Inactive'}
            </span>
            {baseCurrency && (
              <span className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide">
                {baseCurrency}
              </span>
            )}
            {basicInfo.created_date && (
              <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">
                Since {formatSinceLabel(basicInfo.created_date)}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Component ──────────────────────────────────────────────────────────── */

interface ClientDetailProps {
  data: ClientDetailData;
  onBack: () => void;
  /** When true, header is rendered in PageHeader (parent); skip in-page header card */
  suppressHeader?: boolean;
  periodBundle: ClientDetailPeriodBundle;
  /** Bumps when header Clear runs — resets invoice search/status/entity. */
  invoiceFilterResetSeq?: number;
  /** Invoice Details tab reports whether table filters are active (header Clear visibility). */
  onInvoiceTableFiltersActiveChange?: (active: boolean) => void;
}

export const ClientDetail: React.FC<ClientDetailProps> = ({
  data,
  onBack,
  suppressHeader = false,
  periodBundle,
  invoiceFilterResetSeq = 0,
  onInvoiceTableFiltersActiveChange,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const { period, clearSharedPeriod, ts } = periodBundle;

  const basicInfo = data?.contact_profile?.basic_info;
  const baseCurrency =
    data?.metadata?.original_currency ||
    data?.currency_analysis?.primary_currency ||
    'USD';

  const statusRaw = String(basicInfo?.activation_status || '').toLowerCase();
  const isActive = statusRaw === 'active' || statusRaw === 'activate';

  /* ── Empty/loading guard ───────────────────────────────────────── */
  if (!basicInfo) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 shadow-md p-12 text-center">
        <p className="text-slate-600 text-sm">No client data available.</p>
        <button
          onClick={onBack}
          className="mt-4 text-[12px] font-medium text-blue-600 hover:text-blue-800"
        >
          ← Back to list
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {!suppressHeader && (
        <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
          <div className="flex flex-wrap items-start gap-3 px-5 py-3 border-b border-slate-100">
            <button
              type="button"
              onClick={onBack}
              className="flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-lg px-2.5 py-1 hover:bg-slate-50 hover:text-slate-900 transition-colors flex-none self-center"
            >
              <ArrowLeft size={13} /> Back
            </button>

            <div className="flex-1 min-w-0 py-0.5">
              <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                <h2 className="text-base sm:text-[17px] font-bold text-[#0D0D0D] leading-snug">
                  {basicInfo.contact_name}
                </h2>
                {basicInfo.contact_id != null && basicInfo.contact_id !== '' && (
                  <span className="text-[11px] text-slate-600 font-mono">C{basicInfo.contact_id}</span>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                <span
                  className={`text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide ${
                    isActive
                      ? 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200'
                      : 'bg-red-50 text-red-600 ring-1 ring-red-200'
                  }`}
                >
                  {isActive ? '● Active' : basicInfo.activation_status || 'Inactive'}
                </span>
                {baseCurrency && (
                  <span className="bg-blue-50 text-blue-700 ring-1 ring-blue-200 text-[10px] font-bold px-2.5 py-0.5 rounded-full tracking-wide">
                    {baseCurrency}
                  </span>
                )}
                {basicInfo.created_date && (
                  <span className="bg-slate-100 text-slate-500 text-[10px] px-2 py-0.5 rounded-full">
                    Since {formatSinceLabel(basicInfo.created_date)}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── B. Tab bar ──────────────────────────────────────────────── */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-md overflow-hidden">
        <div className="flex border-b border-slate-200 overflow-x-auto">
          {TABS.map((tab) => {
            const Icon = tab.icon;
            const active = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-5 py-3.5 text-[12px] font-semibold whitespace-nowrap transition-all border-b-2 -mb-px flex-none ${
                  active
                    ? 'border-blue-500 text-blue-600 bg-blue-50/30'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
              >
                <Icon size={13} className={active ? 'text-blue-500' : 'text-slate-600'} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ── C. Invoice Details: summaries then table ───────────────── */}
      {activeTab === 0 && (
        <InvoiceDetailsTab
          data={data}
          sharedPeriod={period}
          invoiceFilterResetSeq={invoiceFilterResetSeq}
          onInvoiceTableFiltersActiveChange={onInvoiceTableFiltersActiveChange}
        />
      )}
      {/* ── D. Other tabs ─────────────────────────────────────────── */}
      {activeTab === 1 && (
        <TimesheetProjectsTab
          data={data}
          ts={ts}
          onClearSharedPeriod={clearSharedPeriod}
        />
      )}
      {activeTab === 2 && (
        <ChartsAndTrendsTab data={data} sharedPeriod={period} />
      )}
      {activeTab === 3 && <DepositInfoTab data={data} />}
    </div>
  );
};

export default ClientDetail;
